import React from 'react'

import Immutable from 'immutable'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'

import { getDiscountLines } from 'components/Receipt/getDiscountLines'
import { formatSurcharge } from 'utils/format'

import css from './OrderPricingDetail.css'

const OrderPricingDetail = ({ paymentDate, numberOfRecipes, priceBreakdown }) => {
  const flatDiscountAmount = priceBreakdown.get('flatDiscountAmount', 0)
  const percentageDiscountAmount = priceBreakdown.get('percentageDiscountAmount', 0)
  const grossRecipesPrice = priceBreakdown.get('grossRecipesPrice', '')
  const grossExtrasPrice = priceBreakdown.get('grossExtrasPrice', 0)
  const grossShippingPrice = priceBreakdown.get('grossShippingPrice', 0)
  const netOrderPrice = priceBreakdown.get('netOrderPrice', '')
  const surcharge = priceBreakdown.get('surcharge', 0)

  // Itemised discounts replace the single discount line, so an offer is not counted in the percentage
  const { lines: itemisedLines, showRecipeDiscountLine } = getDiscountLines({
    discounts: priceBreakdown.get('discounts')?.toJS(),
    totalDiscount: flatDiscountAmount,
    canShowRecipeDiscountLine: Boolean(flatDiscountAmount),
  })
  // Pricing can send a decimal string such as "20.00", which Number turns into the "20" we want to show
  const formatDiscountLabel = (percentage) =>
    Number(percentage) ? `${Number(percentage)}% Discount` : 'Discount'
  const discountRows = showRecipeDiscountLine
    ? [
        {
          key: 'discount',
          label: formatDiscountLabel(percentageDiscountAmount),
          amount: flatDiscountAmount,
          dataTesting: 'discountAmount',
        },
      ]
    : itemisedLines.map(({ key, kind, label, amount, percentage }) => ({
        key,
        label: kind === 'offer' ? label : formatDiscountLabel(percentage),
        amount: Number(amount),
        dataTesting: kind === 'offer' ? 'offerSavingsAmount' : 'discountAmount',
      }))

  return (
    <div className={css.paymentInfo} data-testing="recipesPricingDetailSection">
      <div className={`${css.row} ${css.bold}`}>Payment on {paymentDate}</div>
      <div className={css.row}>
        <span>{numberOfRecipes} recipes</span>
        {typeof grossRecipesPrice === 'number' ? (
          <span>£{grossRecipesPrice.toFixed(2)}</span>
        ) : null}
      </div>
      {discountRows.map(({ key, label, amount, dataTesting }, index) => (
        <div
          key={key}
          className={`${css.row} ${css.green} ${
            index === discountRows.length - 1 ? css.separationBelow : ''
          }`}
          data-testing={dataTesting}
        >
          <span>{label}</span>
          <span>
            -£
            {amount.toFixed(2)}
          </span>
        </div>
      ))}
      {surcharge ? (
        <div className={`${css.row} ${css.separationBelow}`}>
          <span>Surcharges and/or savings</span>
          <span>{formatSurcharge(surcharge)}</span>
        </div>
      ) : null}
      {grossExtrasPrice ? (
        <div className={`${css.row} ${css.separationBelow}`}>
          <span>Sides and market items</span>
          <span>£{grossExtrasPrice.toFixed(2)}</span>
        </div>
      ) : null}
      <div className={`${css.row} ${css.separationBelow}`}>
        <span>Delivery cost</span>
        {grossShippingPrice ? <span>£{grossShippingPrice.toFixed(2)}</span> : <span>Free</span>}
      </div>
      <div className={`${css.row} ${css.bold}`}>
        <span>Total</span>
        {typeof netOrderPrice === 'number' ? <span>£{netOrderPrice.toFixed(2)}</span> : null}
      </div>
    </div>
  )
}

OrderPricingDetail.propTypes = {
  paymentDate: PropTypes.string,
  numberOfRecipes: PropTypes.number,
  priceBreakdown: ImmutablePropTypes.mapContains({
    flatDiscountAmount: PropTypes.number,
    percentageDiscountAmount: PropTypes.number,
    discounts: ImmutablePropTypes.list,
    grossRecipesPrice: PropTypes.number,
    grossExtrasPrice: PropTypes.number,
    grossShippingPrice: PropTypes.number,
    netOrderPrice: PropTypes.number,
    surcharge: PropTypes.number,
  }),
}

OrderPricingDetail.defaultProps = {
  paymentDate: '',
  numberOfRecipes: 0,
  priceBreakdown: Immutable.Map({}),
}

export default OrderPricingDetail
